import React, { useEffect, useState } from "react";
import axios from "axios";
import { Clock, UserX, Flame } from "lucide-react";
import ShiftStartModal from "./shift/ShiftStartModal";
import ShiftEndModal from "./shift/ShiftEndModal";
import ShiftTimer from "./shift/ShiftTimer";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * On-duty rota toggle + shift ritual entrypoint.
 *
 * Behaviour:
 *   • No active shift → button "Заступить на смену" opens ShiftStartModal
 *     (mode picker → checklist → goal → start). On success the button
 *     switches to "Я на смене" and the ShiftTimer widget appears.
 *   • Active shift owned by me → clicking opens ShiftEndModal
 *     (close-checklist → auto stats → notes → Telegram report).
 *   • Someone else is on duty → shown for context; clicking asks
 *     confirm to take over (opens the start modal again).
 */
export default function DutyToggle() {
  const [duty, setDuty] = useState({ manager: null, is_me: false });
  const [shift, setShift] = useState(null);   // active shift doc
  const [streak, setStreak] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showStart, setShowStart] = useState(false);
  const [showEnd, setShowEnd] = useState(false);

  const refresh = async () => {
    try {
      const [d, s, st] = await Promise.all([
        axios.get(`${API}/admin/duty/current`, { withCredentials: true }),
        axios.get(`${API}/admin/shift/current`, { withCredentials: true }),
        axios.get(`${API}/admin/shift/streak`, { withCredentials: true }),
      ]);
      setDuty(d.data);
      setShift(s.data?.active || null);
      setStreak(st.data?.streak || 0);
    } catch { /* noop */ }
  };
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, []);

  const base = {
    background: "white", border: "1px solid #e5e7eb", borderRadius: "8px",
    padding: "8px 12px", cursor: "pointer", display: "inline-flex",
    alignItems: "center", gap: "8px", fontFamily: "Manrope", fontSize: "12px",
    fontWeight: 600, transition: "all .15s", opacity: loading ? 0.6 : 1,
  };

  // Active shift owned by me — pool mode also lands here because
  // /admin/shift/current returns MY session regardless of the duty
  // singleton (see shift.py: _my_active). Without this branch, running
  // 2+ managers on shift would flip the label to "Заступить на смену"
  // and clicking would 409 with "У вас уже открыта активная смена".
  if (shift) {
    const isOnline = shift.mode === "online";
    const availableForLeads = shift.available_for_leads !== false;
    const togglePause = async (e) => {
      e.stopPropagation();
      try {
        await axios.patch(`${API}/admin/shift/current`,
          { available_for_leads: !availableForLeads },
          { withCredentials: true });
        refresh();
      } catch (err) {
        alert(err.response?.data?.detail || "Не удалось изменить статус");
      }
    };
    return (
      <>
        <button data-testid="duty-clock-out" onClick={() => setShowEnd(true)}
          title="Смена активна — клик, чтобы закрыть смену и отправить отчёт"
          style={{
            ...base, borderColor: "#C5A059",
            background: "linear-gradient(135deg, rgba(197,160,89,0.08), rgba(212,184,120,0.15))",
            color: "#0A0A0A",
          }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#16a34a",
                         boxShadow: "0 0 6px rgba(22,163,74,0.6)" }} />
          <Clock size={13} />
          Я на смене · {isOnline ? "онлайн" : "бутик"}
          {streak > 0 && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 3,
                            marginLeft: 4, color: "#B45309" }}>
              <Flame size={12} /> {streak}
            </span>
          )}
        </button>
        {isOnline && (
          <button data-testid="duty-available-toggle" onClick={togglePause}
            title={availableForLeads ? "Пауза приёма новых лидов" : "Возобновить приём лидов"}
            style={{
              ...base,
              borderColor: availableForLeads ? "#16a34a" : "#f59e0b",
              background: availableForLeads ? "rgba(22,163,74,0.05)" : "rgba(245,158,11,0.08)",
              color: availableForLeads ? "#166534" : "#92400e",
            }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%",
                            background: availableForLeads ? "#16a34a" : "#f59e0b" }} />
            {availableForLeads ? "Принимаю лиды" : "На паузе"}
          </button>
        )}
        <ShiftTimer shift={shift} onUpdate={refresh} />
        {showEnd && (
          <ShiftEndModal shift={shift}
            onClose={(closed) => { setShowEnd(false); if (closed) refresh(); }} />
        )}
      </>
    );
  }

  // No active shift for me — always offer to start one. Pool mode means
  // multiple managers can be on shift simultaneously (1-2 online + 1-2
  // offline), so seeing "someone else is on duty" is NEVER a reason to
  // hide the CTA for me. Bug reported 25-Feb-2026: admin saw "Смена:
  // Юлия" without a click-target when Юлия was the only one on shift.
  {
    return (
      <>
        <button data-testid="duty-clock-in" onClick={() => setShowStart(true)}
          title="Открыть чек-лист и начать смену"
          style={{
            ...base,
            color: "#374151",
          }}>
          <UserX size={14} />
          <span style={{ maxWidth: 180, overflow: "hidden",
                          textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            Заступить на смену
          </span>
          {streak > 0 && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 3,
                            marginLeft: 4, color: "#B45309" }}>
              <Flame size={12} /> {streak}
            </span>
          )}
        </button>
        {showStart && (
          <ShiftStartModal
            onClose={(started) => { setShowStart(false); if (started) refresh(); }} />
        )}
      </>
    );
  }
}
